import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, FileText, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Templates() {
    const { t } = useLang();
    const { user, session, loading: authLoading } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const inputRef = useRef(null);
    const [templates, setTemplates] = useState([]);
    const [projects, setProjects] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [renderOpen, setRenderOpen] = useState(false);
    const [renderForm, setRenderForm] = useState({ template_id: '', project_id: '' });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!currentWorkspace) return;
        const [{ data: t }, { data: pr }] = await Promise.all([
            supabase.from('documents').select('*').eq('workspace_id', currentWorkspace.id).eq('category', 'template').order('created_at', { ascending: false }),
            supabase.from('projects').select('id,name').eq('workspace_id', currentWorkspace.id).is('deleted_at', null).order('name'),
        ]);
        setTemplates(t || []); setProjects(pr || []);
    };
    useEffect(() => { if (!authLoading && user) load(); /* eslint-disable-next-line */ }, [authLoading, user, currentWorkspace]);

    const upload = async (e) => {
        setError(null);
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/\.(docx|xlsx)$/i.test(f.name)) { setError('Seuls .docx et .xlsx sont supportés'); return; }
        if (f.size > 10 * 1024 * 1024) { setError('Fichier > 10 MB'); return; }
        setUploading(true);
        const path = `${currentWorkspace.id}/_templates/${Date.now()}_${f.name}`;
        const { error: uErr } = await supabase.storage.from('documents').upload(path, f, { cacheControl: '3600', upsert: false });
        if (uErr) { setError(uErr.message); setUploading(false); return; }
        const { error: iErr } = await supabase.from('documents').insert({
            workspace_id: currentWorkspace.id, name: f.name, storage_path: path,
            mime_type: f.type, size_bytes: f.size, uploaded_by: user.id,
            category: 'template', status: 'approved',
            created_by: user.id,
        });
        if (iErr) setError(iErr.message);
        setUploading(false); inputRef.current.value = ''; load();
    };

    const startRender = (template_id) => {
        setRenderForm({ template_id, project_id: '' });
        setRenderOpen(true);
    };

    const renderTemplate = async (e) => {
        e.preventDefault();
        if (!renderForm.project_id) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/templates/render`, {
                document_id: renderForm.template_id,
                project_id: renderForm.project_id,
                workspace_id: currentWorkspace.id,
            }, { headers: { Authorization: `Bearer ${session.access_token}` }, responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href = url; a.download = `rendu_${Date.now()}`; a.click();
            URL.revokeObjectURL(url);
            setRenderOpen(false);
        } catch (e) {
            alert(e.response?.data?.detail || e.message);
        }
        setBusy(false);
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;

    return (
        <div className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-2">Modèles documentaires</div>
                    <h1 className="font-heading text-4xl">{t('templates.title')}</h1>
                    <p className="text-foreground/70 mt-3 max-w-3xl">{t('templates.sub')}</p>
                </div>
                <div>
                    <input ref={inputRef} type="file" accept=".docx,.xlsx" className="hidden" onChange={upload} data-testid="templates-file-input" />
                    <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-primary hover:bg-primary-hover" data-testid="templates-upload-btn">
                        {uploading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-1.5" />} {t('templates.upload')}
                    </Button>
                </div>
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md" data-testid="templates-error">{error}</div>}

            <div className="card-soft p-6">
                <div className="font-heading text-lg mb-2">Variables disponibles</div>
                <p className="text-sm text-foreground/70 mb-3">Utilisez ces placeholders dans vos fichiers .docx ou .xlsx :</p>
                <div className="flex flex-wrap gap-1.5">
                    {['{{workspace.name}}', '{{project.name}}', '{{project.code}}', '{{project.status}}', '{{project.progress}}', '{{project.budget_total}}', '{{project.currency}}', '{{budget.planned}}', '{{budget.spent}}', '{{budget.remaining}}', '{{now.date}}', '{{now.year}}'].map(v => (
                        <code key={v} className="text-xs bg-surface-muted px-2 py-1 rounded font-mono">{v}</code>
                    ))}
                </div>
            </div>

            {templates.length === 0 ? (
                <div className="card-soft p-10 text-center text-foreground/60" data-testid="templates-empty">{t('templates.empty')}</div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {templates.map(t => (
                        <div key={t.id} className="card-soft p-5" data-testid={`template-${t.id}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="h-10 w-10 rounded-lg bg-primary-light text-primary flex items-center justify-center"><FileText size={18} strokeWidth={1.6} /></div>
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{t.name}</div>
                                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                            <Button onClick={() => startRender(t.id)} size="sm" className="w-full bg-primary hover:bg-primary-hover" data-testid={`template-render-${t.id}`}>
                                <Download size={12} className="mr-1.5" /> Générer pour un projet
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={renderOpen} onOpenChange={setRenderOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Générer le document</DialogTitle></DialogHeader>
                    <form onSubmit={renderTemplate} className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Projet à utiliser</label>
                            <select required value={renderForm.project_id} onChange={(e) => setRenderForm({ ...renderForm, project_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="template-render-project">
                                <option value="">— Choisir un projet —</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setRenderOpen(false)}>{t('common.cancel')}</Button>
                            <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="template-render-submit">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : 'Générer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
